import type { ZodTypeAny } from 'zod';
import type { IUserInfoExtended } from '~/interfaces/dto/auth/response';
import type { IBankingAccountSettingsRequest } from '../dto/auth/request';
import type { Dayjs } from 'dayjs';

export type TStyleClasses = Record<string, boolean>;

export interface IFormBuilderError {
  readonly code: string;
  readonly message: string;
  readonly field?: string;
}

export type TTemplateName =
  | 'add number'
  | 'add personal details'
  | 'complete registration'
  | 'confirm id'
  | 'enter otp'
  | 'favourite games survey'
  | 'location and interest survey'
  | 'login'
  | 'manage banking'
  | 'manage communication preferences'
  | 'otp options'
  | 'partial registration'
  | 'personal details'
  | 'registration'
  | 'reset password'
  | 'security questions'
  | 'streaming academy registration'
  | 'update email'
  | 'update number'
  | 'update password';

export interface IFormBuilderValidator {
  [key: string]: ZodTypeAny;
}
export enum ECommunicationChannel {
  NONE,
  SMS,
  EMAIL,
}

export interface IFormBuilderItem {
  value: string;
  label: string;
}

export interface IBuilderProps {
  templateName: TTemplateName;
  readonly showTitle?: boolean;
  readonly showSubTitle?: boolean;
  readonly shouldAutoFocus?: boolean;
  titleClasses?: TStyleClasses[];
  subTitleClasses?: TStyleClasses[];
  containerClasses?: TStyleClasses[];
  inputBinding?: { [key: string]: boolean | string };
  formClasses?: TStyleClasses[];
  inputContainerClasses?: TStyleClasses[];
  stepContainerClasses?: TStyleClasses[];
  elementContainerClasses?: TStyleClasses[];
}

export type TModelValue =
  | string
  | number
  | boolean
  | Dayjs
  | string[]
  | number[]
  | Partial<IUserInfoExtended>
  | Record<string, unknown>
  | IBankingAccountSettingsRequest;

export interface IDateOfBirthFromID {
  shortYear: number | string;
  birthDateIdentifier: number | string;
  year: number | string;
  month: number | string;
  day: number | string;
}

export interface IFormBuilderReference {
  model: TModelValue;
  derivedModel: Record<string, TModelValue>;
  loaded: boolean;
  validated: boolean;
  invalid: boolean;
  formErrors: { [key: string]: string };
  schema: ITranspiledSchema;
  validator: IFormBuilderValidator;
  isEmpty: boolean;
  hasRecaptcha: boolean;
  steps: {
    activeStep: number;
    isAtFirstStep: boolean;
    isAtLastStep: boolean;
    availableSteps: {
      validated: boolean;
      invalid: boolean;
      stepModel: { [p: string]: string | number | boolean | Date | string[] };
    }[];
    next: () => void;
    previous: () => void;
  };
}

/*WMS RESPONSE TYPES*/
export interface IWMSResponse {
  readonly formId: number;
  readonly formCodeName: string;
  readonly formTitle: TTemplateName;
  readonly formSubTitle: string;
  readonly regionCode: string;
  readonly sections: readonly ISection[];
}
export interface ISection {
  formSectionId: number;
  formSectionCodeName: string;
  formSectionTitle: string;
  formSectionSubTitle: string;
  formSectionOrder: number;
  rows: IRow[];
}
export interface IRow {
  rowNumber: number;
  fields: IField[];
}
export interface IField {
  fieldId: number;
  fieldIdentifier: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  fieldPlaceholder?: string;
  inputType: string;
  textStyle?: string;
  validationMessage: string;
  isVisible: boolean;
  isRequired: boolean;
  isReadOnly: boolean;
  fieldRegex: string;
  prefix: string;
  suffix: string;
  fieldDropdowns?: IFieldDropdown[];
  fieldRadioGroup?: IFieldRadioGroup[];
}
export interface IFieldDropdown {
  value: string;
  text: string;
  regex?: string;
}
export interface IFieldRadioGroup {
  value: string;
  text: string;
}

/*FORMBUILDER TRANSPILED TYPES*/
export interface IFormBuilderSchema {
  isEmpty: boolean;
  schema: ITranspiledSchema;
  formSubTitle: string;
  formTitle: TTemplateName;
  hasRecaptcha: boolean;
}
export interface ITranspiledSchema {
  hasSteps: boolean;
  sectionMapping: ISectionMapping[];
}
export interface ISectionMapping {
  id: number;
  sectionTitle: string;
  sectionSubtitle: string;
  elementMapping: IElementMapping;
}
export interface IElementMapping {
  id: number;
  elements: IElement[];
}
export type TElementType =
  | 'input'
  | 'dropdown'
  | 'checkbox'
  | 'radio group'
  | 'toggle';
export type TInputType =
  | 'dropdown'
  | 'text'
  | 'textarea'
  | 'tel'
  | 'number'
  | 'email'
  | 'password'
  | 'dropDownTextInput'
  | 'calendar'
  | 'checkbox';
export interface IElement extends IField {
  messages: IMessages;
  rules: string[];
  type: TElementType;
  inputType: TInputType;
  disabled: boolean;
  required: boolean;
  placeholder: string;
  name: string;
  label: string;
  id: string;
  regex: string;
  items?: IFormBuilderItem[];
  props: {
    [key: string]: string | boolean | number | Dayjs | IFormBuilderItem[];
  };
}

export interface IMessages {
  [key: string]: string;
}

export interface IGenericFormField {
  propertyName: string;
  propertyValue: string;
}

export interface IGenericFormPayload {
  formType: string;
  regionCode: string;
  formFields: IGenericFormField[];
  brand?: 'JackpotCity';
}
